/**
 * Monitor leghe per l'admin dell'app (Andrea 2026-10-06): tutte le leghe,
 * chi c'e' dentro e se c'e' qualcosa da sistemare. Per intervenire si entra
 * nella lega in supervisione (vedi `useEntraInSupervisione`).
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { type User, useAuthStore } from "../lib/auth-store";

export interface Segnalazione {
  livello: "problema" | "attenzione" | "info";
  testo: string;
}

export interface StagioneBreve {
  id: string;
  anno: string;
  stato: string;
  modalita: string;
}

export interface LegaMonitor {
  id: string;
  nome: string;
  stato: string;
  is_default: boolean;
  codice_invito: string | null;
  created_at: string;
  fondatore: string | null;
  amministratori: string[];
  partecipanti: string[];
  partecipo: boolean;
  n_membri: number;
  n_squadre: number;
  n_squadre_libere: number;
  stagione: StagioneBreve | null;
  giornate_totali: number;
  giornate_calcolate: number;
  ultima_calcolata: number | null;
  prossima_da_giocare: number | null;
  competizioni: string[];
  salute: "ok" | "attenzione" | "problema";
  segnalazioni: Segnalazione[];
}

export interface LegaDettaglio extends LegaMonitor {
  membri: {
    user_id: string;
    nome: string;
    email: string;
    ruoli: string[];
    is_fondatore: boolean;
    attivo: boolean;
    squadra: string | null;
    secondo_di: string | null;
    ultimo_accesso: string | null;
  }[];
  squadre: { id: string; nome: string; presidente: string | null; uscita: boolean }[];
  stagioni: StagioneBreve[];
  competizioni_dettaglio: {
    id: string;
    nome: string;
    tipo: string;
    stato: string;
    stagione: string;
  }[];
}

export function useMonitorLeghe() {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["monitor-leghe"],
    queryFn: async () => (await api.get<LegaMonitor[]>("/admin-app/leghe")).data,
    enabled: isAuth,
    refetchOnWindowFocus: true,
  });
}

export function useDettaglioLega(legaId: string | null) {
  return useQuery({
    queryKey: ["monitor-leghe", legaId],
    queryFn: async () => (await api.get<LegaDettaglio>(`/admin-app/leghe/${legaId}`)).data,
    enabled: !!legaId,
  });
}

/** Entra in una lega (anche se non se ne e' membri: supervisione admin app)
 * oppure ne esce (legaId null = torna alla propria lega). */
export function useEntraInSupervisione() {
  const setLegaId = useAuthStore((s) => s.setLegaId);
  const setUser = useAuthStore((s) => s.setUser);
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async (legaId: string | null, to: string) => {
    setLegaId(legaId);
    const me = await api.get<User>("/auth/me");
    setUser(me.data);
    qc.clear();
    navigate(to, { replace: true });
  };
}
